import { IconEnum } from '@pdcfrontendui/components';
import AccordionContent from '@pdcfrontendui/components/AccordionItem/AccordionContent';
import { ExchangeTypeEnum, TeamShiftStatusEnum } from '@pdcplan/shared/enums';
import { Person, Team, TeamShift } from '@pdcplan/shared/api/TeamPlan';
import ids from '../../testing/ids';
import { currentLanguage } from '../../currentLanguage';
import scss from './ActionRequiredCard.module.scss';
import {
  ShiftActionButton,
  ShiftActionButtonVariant,
} from '../ShiftActionButton';
import classNames from 'classnames';
import { ShiftInfo } from '../ShiftCard';
import { shiftRequiresAction } from '../../util/shifts';
import { useEffect, useRef } from 'react';

/**
 * Should always be rendered
 */
export function ActionRequiredCard({
  team,
  shift,
  employee,
  className,
  callFindSubstitute,
  callMarkAsHandled,
  openOfferView,
  isCreatedShift,
  isAssigningShift,
  openEditShift,
  exchangeType,
  loadingSwap,
  openSwapView,
  cancelOfferOnShiftExchange,
}: {
  team: Team;
  shift: TeamShift;
  employee?: Person;
  isCreatedShift: boolean;
  className?: string;
  isAssigningShift: boolean;
  exchangeType: ExchangeTypeEnum | null;
  loadingSwap: boolean;
  openOfferView: () => void;
  callFindSubstitute: (
    teamId: string,
    shiftId: string,
    personId: number
  ) => void;
  callMarkAsHandled: (
    teamId: string,
    shiftId: string,
    personId: number
  ) => void;
  openEditShift?: () => void;
  openSwapView: () => void;
  cancelOfferOnShiftExchange: () => void;
}) {
  const prevShown = useRef<
    | null
    | (TeamShift & {
        status:
          | TeamShiftStatusEnum.actionRequired
          | TeamShiftStatusEnum.offerActionRequired
          | TeamShiftStatusEnum.swapActionRequired
          | TeamShiftStatusEnum.offered;
      })
  >(null);
  // Logic here is based on the latest iteration of the shift where action is required.
  // This is so that animating out runs smoothly, and data doesn't change when animation starts.
  // Can only be null until the component is shown for the first time.
  const latest = shiftRequiresAction(shift) ? shift : prevShown.current;
  const expanded =
    shiftRequiresAction(shift) && !isCreatedShift && !isAssigningShift;

  const showGoToSwapOrChooseApplicants =
    latest?.status === TeamShiftStatusEnum.offerActionRequired ||
    latest?.status === TeamShiftStatusEnum.swapActionRequired;

  const isFictive = !!employee?.isFictive;

  const assigningCreated = isCreatedShift || isFictive;

  const showOfferOnShiftExchange =
    latest?.status === TeamShiftStatusEnum.actionRequired &&
    (!isCreatedShift || isFictive);
  const showMarkAsHandled =
    latest?.status === TeamShiftStatusEnum.actionRequired && !assigningCreated;
  const isSwapRequest = exchangeType === ExchangeTypeEnum.swapRequest;

  useEffect(() => {
    if (shiftRequiresAction(shift)) {
      prevShown.current = shift;
    }
  }, [shift]);

  const displayed = latest ?? shift;

  return (
    <AccordionContent
      expanded={expanded}
      id={ids.ShiftView.actionRequiredCard}
      outerClass={classNames(scss.comp, className, !expanded && scss.hidden)}
      innerClass={scss.inner}
      fadeStartMult={0.9}
      duration={500}
    >
      <ShiftInfo
        dutyLines={
          displayed.dutyLinesOriginal?.length
            ? displayed.dutyLinesOriginal
            : displayed.dutyLines
        }
        shiftDraft={null}
        shift={displayed}
        isAssigningShift={false}
        swapDetails={null}
      >
        {!isCreatedShift && !isAssigningShift && (
          <>
            {showOfferOnShiftExchange && (
              <ShiftActionButton
                id={ids.ShiftView.exchange}
                onClick={openOfferView}
                disabled={!team.canOffer}
                icon={IconEnum.Swap}
                variant={ShiftActionButtonVariant.Primary}
                warning={
                  !team.canOffer
                    ? currentLanguage.MissingSwapGroup_1(
                        employee?.initials.toUpperCase() ||
                          employee?.name ||
                          currentLanguage.Employee
                      )
                    : undefined
                }
              >
                {currentLanguage.offerOnShiftExchange}
              </ShiftActionButton>
            )}
            {showGoToSwapOrChooseApplicants && (
              <ShiftActionButton
                id={ids.ShiftView.goToSwap}
                onClick={openSwapView}
                variant={ShiftActionButtonVariant.Primary}
                icon={IconEnum.Swap}
                disabled={loadingSwap}
                aria-label={
                  isSwapRequest
                    ? currentLanguage.GoToSwap
                    : currentLanguage.GoToOffer
                }
              >
                {isSwapRequest
                  ? currentLanguage.GoToSwap
                  : currentLanguage.GoToOffer}
              </ShiftActionButton>
            )}
            {(latest?.status === TeamShiftStatusEnum.offerActionRequired ||
              latest?.status === TeamShiftStatusEnum.offered) && (
              <ShiftActionButton
                id={ids.ShiftView.exchange}
                onClick={cancelOfferOnShiftExchange}
                icon={IconEnum.ArrowRightSlash}
                variant={ShiftActionButtonVariant.Danger}
              >
                {currentLanguage.cancelOfferOnShiftExchange}
              </ShiftActionButton>
            )}
            {latest?.status === TeamShiftStatusEnum.actionRequired && (
              <ShiftActionButton
                id={ids.ShiftView.substitute}
                onClick={() => {
                  callFindSubstitute(team.id, shift.id, shift.personId);
                }}
                icon={IconEnum.User}
                variant={ShiftActionButtonVariant.Primary}
              >
                {assigningCreated
                  ? currentLanguage.FindEmployee
                  : currentLanguage.findSubstitute}
              </ShiftActionButton>
            )}
            {latest?.status === TeamShiftStatusEnum.actionRequired &&
              openEditShift && (
                <ShiftActionButton
                  onClick={openEditShift}
                  icon={IconEnum.Edit}
                  variant={ShiftActionButtonVariant.Primary}
                >
                  {currentLanguage.EditAndFindSubstitute}
                </ShiftActionButton>
              )}
            {showMarkAsHandled && (
              <ShiftActionButton
                id={ids.ShiftView.handled}
                onClick={() => {
                  callMarkAsHandled(team.id, shift.id, shift.personId);
                }}
                icon={IconEnum.Checkmark}
                variant={ShiftActionButtonVariant.Primary}
              >
                {currentLanguage.markAsHandled}
              </ShiftActionButton>
            )}
          </>
        )}
      </ShiftInfo>
    </AccordionContent>
  );
}
